import { Fragment, useState, useEffect } from 'react';
import { Table as BootstrapTable, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import capitalizeFirstLetter from '../util/char-helper';
import TablePagination from './TablePagination/TablePagination';

// TODO: ADD SORTING
export default function Table({
  title,
  name,
  columns,
  dataSource,
  rowComponent,
  creationLink,
  tableControls,
  isLoading,
  isError,
  actionWidth,
  renderError,
  enablePagination,
  pagination,
  onChange,
  showEmptyRows,
  ...props
}) {
  const [page, setPage] = useState(pagination?.page || 1);
  const [pageSize, setSize] = useState(pagination?.pageSize || 10);
  const [maxPages, setMaxPages] = useState(pagination?.pages || 1);

  const checkPages = (size = pageSize) => {
    let currentPages = Math.floor(dataSource.length / size);
    if (dataSource.length % size > 0) currentPages += 1;
    return currentPages;
  };

  useEffect(() => {
    if (!isLoading && dataSource.length > 0) {
      setMaxPages(checkPages());
    }
  }, [isLoading, pageSize, dataSource.length]);

  const handleChangeSize = (size) => {
    const newSize = Number(size);
    setPage(1);
    setSize(newSize);
    setMaxPages(checkPages(newSize));
    if (onChange) {
      onChange({ pageSize: newSize, page });
    }
  };

  const handleChangePage = (nextPage) => {
    setPage(nextPage);
    if (onChange) {
      onChange({ pageSize, page: nextPage });
    }
  };

  const renderColumnHead = (label) => <th key={`${name}_${label}`}>{label}</th>;

  const renderLoading = () => (
    <tr>
      <td colSpan="9">
        <div className="text-muted text-center m-0">
          <Spinner size="sm" animation="border" /> Loading...
        </div>
      </td>
    </tr>
  );

  const renderEmptyResult = () => (
    <tr>
      <td colSpan="10">
        <p
          className="text-muted mb-0 text-center"
          data-testid="empty-result-message"
          aria-label='Not listed'
        >
          No {name} listed.
        </p>
      </td>
    </tr>
  );

  const renderInnerError = () => (
    <tr>
      <td colSpan="10">{renderError()}</td>
    </tr>
  );

  const renderRowItem = (row, index) => {
    const TableRow = rowComponent;

    return (
      <Fragment key={`${row?.id}_${index}` || index}>
        <TableRow {...{ [name]: row, index }} />
      </Fragment>
    );
  };

  const renderTableRows = () => {
    let result = dataSource.map(renderRowItem);

    if (!onChange && enablePagination) {
      const items = dataSource.slice(pageSize * (page - 1), page * pageSize);

      result = [...items.map(renderRowItem)];
      if (showEmptyRows) {
        result.push(
          ...Array.from(Array(pageSize - items.length).keys()).map((val) => (
            <Fragment key={`TABLE_EMPTY_ROW_${val}`}>
              <tr>
                {Array.from(
                  Array(columns.length + (actionWidth ? 1 : 0)).keys()
                ).map(() => (
                  <td>&nbsp;</td>
                ))}
              </tr>
            </Fragment>
          ))
        );
      }
    }

    return result;
  };

  const renderContent = () => {
    let result;
    if (!isError) {
      if (!isLoading) {
        if (dataSource.length > 0) {
          result = renderTableRows();
        } else {
          result = renderEmptyResult();
        }
      } else {
        result = renderLoading();
      }
    } else {
      result = renderInnerError();
    }
    return result;
  };

  return (
    <div className="d-flex flex-column" data-testid="Wrapper">
      <div className="d-flex flex-row justify-content-between mb-3">
        <div className="d-inline-flex">
          <h2 className="pe-2">{title}</h2>
          {tableControls}
        </div>
        {creationLink && (
          <div>
            <Link to={creationLink} className="mt-2 btn btn-primary btn-sm">
              Add {name ==='competency' ? 'Specialty' : capitalizeFirstLetter(name)}
            </Link>
          </div>
        )}
      </div>
      <div className="table-responsive">
        <BootstrapTable {...props}>
          <thead>
            <tr>
              {columns.map(renderColumnHead)}
              {actionWidth && <th style={{ width: actionWidth }}>Actions</th>}
            </tr>
          </thead>
          <tbody>{renderContent()}</tbody>
        </BootstrapTable>
      </div>
      {enablePagination && (
        <TablePagination
          page={page}
          pages={pagination?.pages || maxPages}
          pageSize={pageSize}
          onChangePage={handleChangePage}
          onChangeSize={handleChangeSize}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

Table.propTypes = {
  enablePagination: PropTypes.bool,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  showEmptyRows: PropTypes.bool,
  name: PropTypes.string,
  title: PropTypes.string,
  actionWidth: PropTypes.string,
  creationLink: PropTypes.string,
  dataSource: PropTypes.arrayOf(PropTypes.shape({})),
  columns: PropTypes.arrayOf(PropTypes.string),
  rowComponent: PropTypes.func,
  tableControls: PropTypes.node,
  renderError: PropTypes.func,
  onChange: PropTypes.func,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    pages: PropTypes.number,
    pageSize: PropTypes.number,
  }),
};

Table.defaultProps = {
  showEmptyRows: false,
  enablePagination: false,
  isLoading: false,
  isError: false,
  title: '',
  name: '',
  actionWidth: '80px',
  dataSource: [],
  columns: [],
  rowComponent: null,
  creationLink: null,
  tableControls: null,
  renderError: undefined,
  pagination: {},
  onChange: undefined,
};
